import {TStimMetadata} from "@/common/types/TStimConfig.ts";
import {Dayjs} from "dayjs";
import {IRawFile, TSubjectId} from "@/common/types";
import {
    IProtocolDefinition, IProtocolDefinitionBase, IProtocolStepDefinition, IProtocolStepDefinitionCommonFields,
    TProtocolDefinitionId, IDirConfig
} from "./protocolDefinitions";
import {IdType} from "@/utils/types/id.ts"
import {TAnalysisGroup} from "./analysis";
import {stepBaseFields} from "./stepMetadataSchemas";
import {validateStepMetadata} from "./stepMetadataValidation";
import {IQuestionnaireQAndAPair} from "./questionnaire";

// Re-export IProtocolStepKind from the base types file
export {IProtocolStepKind} from "./stepKind";
import {IProtocolStepKind} from "./stepKind";
import {IProtocolRawFile} from "./IProtocolRawFile.ts";

export type TProtocolId = IdType<string, "TProtocolId">
export type TProtocolStepId = IdType<string, "TProtocolStepId">

export enum TProtocolStatus {
    IN_PROGRESS = "in_progress",
    COMPLETED = "completed",
}

export interface IProtocol extends IProtocolDefinitionBase {
    id: TProtocolId
    pdefId: TProtocolDefinitionId
    subjectId: TSubjectId
    status: TProtocolStatus
    steps: Array<IProtocolStep>
}

export enum IProtocolStepStatus {
    INCOMPLETE = "incomplete",
    COMPLETED = "completed",
    SKIPPED = "skipped"
}

export interface IProtocolStepCommonFields extends IProtocolStepDefinitionCommonFields {
    status: IProtocolStepStatus
    startTime?: Dayjs
    endTime?: Dayjs
    transient?: {
        fileData?: IStepFiles
    }
}

export interface IProtocolStepBase extends IProtocolStepCommonFields {
    id: TProtocolStepId
    protocolId: TProtocolId
}


export interface IStepFiles {
    files?: Array<IProtocolRawFile>
    uploadProgress?: Record<string, IFileUploadStatus>
}

/**
 * A file that has been requested to upload to the server but has not
 * been fully uploaded yet. Contains the Server view of file, local file,
 * upload request information and current progress.
 * Once complet eit will be moved to official step known files
 */
export interface IFileUploadStatus {
    file: IProtocolRawFile | IRawFile
    uploadUrl: string
    expiresAt: Dayjs
    progress: number
}

export interface IMegScanStep extends IProtocolStepBase {
    kind: IProtocolStepKind.MEG_SCAN
    tags?: Array<string>
    duration?: number
}

export interface IEEGScanStep extends IProtocolStepBase {
    kind: IProtocolStepKind.EEG_SCAN
    tags?: Array<string>
    duration?: number
}

export interface IECGScanStep extends IProtocolStepBase {
    kind: IProtocolStepKind.ECG_SCAN
    tags?: Array<string>
    duration?: number
}

export interface IMeasureBloodPressureReading {
    systolic?: number;
    diastolic?: number;
    capturedAt?: string;  // ISO 8601 timestamp
}

export interface IMeasureBloodPressureStep extends IProtocolStepBase {
    kind: IProtocolStepKind.MEASURE_BP
    count: number;
    readings: IMeasureBloodPressureReading[];
}

export interface IPupillometryReading {
    is_left: boolean;
    captured_at?: string;
    device_time?: string;
}

export interface IMeasurePLRStep extends IProtocolStepBase {
    kind: IProtocolStepKind.MEASURE_PLR
    count: number;
    readings: IPupillometryReading[];
}

export interface IMeasureHeartRateStep extends IProtocolStepBase {
    kind: IProtocolStepKind.MEASURE_HR
    bpm?: number
}

export enum TStimLocation {
    Median = "median",
    Auricular = "auricular",
    Cervical = "cervical",
    Visceral = "visceral",
    Thoracic = "thoracic",
    Tens = "tens",
    Spine = "spine"
}

export interface IStimStep extends IProtocolStepBase {
    kind: IProtocolStepKind.STIM_EVENT
    stimMetadata?: TStimMetadata
    tags?: Array<string>
}

export interface IQuestionAnswerMetricStep extends IProtocolStepBase {
    kind: IProtocolStepKind.Q_AND_A
    question?: string
    answer?: string
}

export interface IQuestionnaireStep extends IProtocolStepBase {
    kind: IProtocolStepKind.QUESTIONNAIRE
    items: IQuestionnaireQAndAPair[]
}

export interface INoteStep extends IProtocolStepBase {
    kind: IProtocolStepKind.NOTE
    note?: string
}

export interface IRestStep extends IProtocolStepBase {
    kind: IProtocolStepKind.REST_EVENT
    duration?: number
}

export interface IStartDataCollectionStep extends IProtocolStepBase {
    kind: IProtocolStepKind.START_DATA_COLLECTION
    data_dir_path?: string
    dir_configs?: IDirConfig[]
    aria?: {
        adapter_placement?: "Left" | "Right"
        analysisGroups: Array<TAnalysisGroup>
    }
}

export interface IStopDataCollectionStep extends IProtocolStepBase {
    kind: IProtocolStepKind.STOP_DATA_COLLECTION
}

export interface IGroupStep extends IProtocolStepBase {
    kind: IProtocolStepKind.GROUP
    memberStepIds: Array<TProtocolStepId>
}

export interface IInstructionStep extends IProtocolStepBase {
    kind: IProtocolStepKind.INSTRUCTIONS
}

export interface IVasPainScoreStep extends IProtocolStepBase {
    kind: IProtocolStepKind.VAS_PAIN_SCORE
    score?: number
}

export interface INRSPainScoreStep extends IProtocolStepBase {
    kind: IProtocolStepKind.NRS_PAIN_SCORE
    score?: number
}

export interface IPPReliefStep extends IProtocolStepBase {
    kind: IProtocolStepKind.PP_RELIEF
    score?: number
}

export interface IPortMap {
    label: string;
    sub_subject: string;
}

export interface IPortConfigStep extends IProtocolStepBase {
    kind: IProtocolStepKind.PORT_CONFIG
    count: number;
    ports: IPortMap[];
}

export type IProtocolStep = IMegScanStep | IEEGScanStep | IECGScanStep |
    IMeasureBloodPressureStep | IMeasureHeartRateStep | IStimStep |
    IQuestionAnswerMetricStep | IQuestionnaireStep | INoteStep | IRestStep | IStartDataCollectionStep | IStopDataCollectionStep | IGroupStep | IInstructionStep | IVasPainScoreStep | INRSPainScoreStep | IPPReliefStep | IPortConfigStep | IMeasurePLRStep;

// unique fields for each type of step
type TMegScanStepMetadata = Omit<IMegScanStep, keyof IProtocolStepBase>
type TEEGScanStepMetadata = Omit<IEEGScanStep, keyof IProtocolStepBase>
type TECGScanStepMetadata = Omit<IECGScanStep, keyof IProtocolStepBase>
type TMeasureBloodPressureStepMetadata = Omit<IMeasureBloodPressureStep, keyof IProtocolStepBase>
type TMeasurePLRStepMetadata = Omit<IMeasurePLRStep, keyof IProtocolStepBase>
type TMeasureHeartRateStepMetadata = Omit<IMeasureHeartRateStep, keyof IProtocolStepBase>
type TStimStepMetadata = Omit<IStimStep, keyof IProtocolStepBase>
type TQuestionAnswerMetricStepMetadata = Omit<IQuestionAnswerMetricStep, keyof IProtocolStepBase>
type TQuestionnaireStepMetadata = Omit<IQuestionnaireStep, keyof IProtocolStepBase>
type TNoteStepMetadata = Omit<INoteStep, keyof IProtocolStepBase>
type TRestStepMetadata = Omit<IRestStep, keyof IProtocolStepBase>
type TStartDataCollectionStepMetadata = Omit<IStartDataCollectionStep, keyof IProtocolStepBase>
type TStopDataCollectionStepMetadata = Omit<IStopDataCollectionStep, keyof IProtocolStepBase>
type TGroupStepMetadata = Omit<IGroupStep, keyof IProtocolStepBase>
type TInstructionStepMetadata = Omit<IInstructionStep, keyof IProtocolStepBase>
type TVasPainScoreStepMetadata = Omit<IVasPainScoreStep, keyof IProtocolStepBase>
type TNRSPainScoreStepMetadata = Omit<INRSPainScoreStep, keyof IProtocolStepBase>
type TPPReliefStepMetadata = Omit<IPPReliefStep, keyof IProtocolStepBase>
type TPortConfigStepMetadata = Omit<IPortConfigStep, keyof IProtocolStepBase>

export type TProtocolStepMetadata =
    TMegScanStepMetadata
    | TEEGScanStepMetadata
    | TECGScanStepMetadata
    | TMeasureBloodPressureStepMetadata
    | TMeasurePLRStepMetadata
    | TMeasureHeartRateStepMetadata
    | TStimStepMetadata
    | TQuestionAnswerMetricStepMetadata
    | TQuestionnaireStepMetadata
    | TNoteStepMetadata
    | TRestStepMetadata
    | TStartDataCollectionStepMetadata
    | TStopDataCollectionStepMetadata
    | TGroupStepMetadata
    | TInstructionStepMetadata
    | TVasPainScoreStepMetadata
    | TNRSPainScoreStepMetadata
    | TPPReliefStepMetadata
    | TPortConfigStepMetadata;

/**
 * Extract all of the fields that should be stored in the Servers Metadata
 * field.
 *
 * @param {IProtocolStep} step - The protocol step object containing detailed information about the step.
 * @returns {object} The extracted metadata object with the non-metadata properties omitted.
 */
export const extractProtocolStepMetadata = (step: IProtocolStep): TProtocolStepMetadata => {
    const metadata: Record<string, unknown> = {};

    for (const key of Object.keys(step)) {
        if (!(stepBaseFields as readonly string[]).includes(key)) {
            metadata[key] = (step as unknown as Record<string, unknown>)[key];
        }
    }

    // Validate and return metadata
    return validateStepMetadata(step.kind, metadata, "extractProtocolStepMetadata") as TProtocolStepMetadata;
}

export const isProtocol = (protocol: IProtocol | IProtocolDefinition): protocol is IProtocol => {
    return ("subjectId" in protocol)
}

/**
 * Type guard to check if a step is an IProtocolStep (extends IProtocolStepBase)
 * vs an IProtocolStepDefinition. IProtocolStep has 'status' field from IProtocolStepCommonFields.
 */
export const isProtocolStep = (step: IProtocolStep | IProtocolStepDefinition): step is IProtocolStep => {
    return ("status" in step)
}

/**
 * Required fields when creating a new protocol step.
 */
export interface ICreateProtocolStepParams {
    kind: IProtocolStepKind;
    id: TProtocolStepId;
    protocolId: TProtocolId;
    name: string;
    status: IProtocolStepStatus;
    createdAt: string;
}

/**
 * Factory function to create a protocol step with proper typing.
 * Enforces required fields while allowing optional metadata fields.
 *
 * @param params - Required base fields for the step
 * @param metadata - Optional metadata fields specific to the step kind
 * @returns A properly typed protocol step
 */
export const createProtocolStep = <T extends IProtocolStep>(
    params: ICreateProtocolStepParams,
    metadata?: Partial<Omit<T, keyof IProtocolStepBase>>
): T => {
    const step: IProtocolStepBase = {
        kind: params.kind,
        id: params.id,
        protocolId: params.protocolId,
        name: params.name,
        status: params.status,
        createdAt: params.createdAt,
    };

    return {
        ...step,
        ...metadata,
    } as T;
};
